import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock3 } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import { useLenis, useHashScroll } from "@/hooks/usePageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("all");
  useLenis();
  useHashScroll();

  useEffect(() => {
    axios.get(`${API}/blogs`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const cats = ["all", ...new Set(posts.map((p) => p.category))];
  const filtered = cat === "all" ? posts : posts.filter((p) => p.category === cat);

  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24" data-testid="blog-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-600 mb-5"
          >
            Chapter 04 / The S/4HANA Journal
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-6 max-w-2xl"
          >
            Field Notes from Real SAP Finance Projects.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl mb-12"
          >
            Deep-dive guides on RAR, Treasury, Bank Integration, Group Reporting, and Controlling — written from implementations, not documentation.
          </motion.p>

          <div className="flex flex-wrap gap-2.5 mb-12" data-testid="blog-category-filters">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`font-mono2 text-[11px] sm:text-xs tracking-wider px-5 py-2.5 rounded-full border transition-all duration-300 ${
                  cat === c ? "bg-amber-400 text-slate-900 border-amber-400 font-bold" : "border-slate-200 text-slate-600 hover:border-amber-600/50 hover:text-amber-700"
                }`}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c === "all" ? "All Posts" : c}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.07 }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  className="group flex flex-col h-full rounded-3xl border border-slate-200 bg-white p-7 hover:-translate-y-1.5 hover:border-amber-600/40 hover:shadow-[0_20px_60px_-20px_rgba(217,119,6,0.18)] transition-all duration-500"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <span className="self-start font-mono2 text-[9px] uppercase tracking-[0.18em] text-sky-600 border border-sky-600/30 bg-sky-600/10 px-3 py-1 rounded-full mb-5">
                    {p.category}
                  </span>
                  <h2 className="font-display text-lg font-bold tracking-tight leading-snug mb-3 group-hover:text-amber-700 transition-colors">{p.title}</h2>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6 flex-1">{p.excerpt}</p>
                  <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                    <span className="font-mono2 text-[10px] text-slate-500 flex items-center gap-1.5">
                      <Clock3 size={11} /> {p.read_time} • {fmtDate(p.date)}
                    </span>
                    <ArrowUpRight size={14} className="text-slate-500 group-hover:text-amber-700 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <BookingModal />
    </>
  );
};

export default BlogPage;
